import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import {
    LayoutDashboard,
    CreditCard,
    Activity,
    Settings,
    Users,
    Trophy,
    BookOpen,
    LogOut,
    Menu,
    X,
    Shield
} from 'lucide-react'

const SUPER_ADMIN_EMAIL = 'pascal.teunissen@gmail.com'

export const PlatformLayout = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [loading, setLoading] = useState(true)
    const [user, setUser] = useState(null)

    useEffect(() => {
        checkSuperAdmin()
    }, [])

    const checkSuperAdmin = async () => {
        const { data: { user } } = await supabase.auth.getUser()

        if (!user || user.email !== SUPER_ADMIN_EMAIL) {
            // Not a super admin? Kick them out.
            // But allow failopen for dev if needed (remove in prod)
            console.warn('Unauthorized access attempt to platform area')
            alert('Geen toegang: Alleen voor Super Admins')
            navigate('/admin/dashboard')
            return
        }

        setUser(user)
        setLoading(false)
    }

    const handleLogout = async () => {
        await supabase.auth.signOut()
        navigate('/login')
    }

    const menuItems = [
        {
            section: 'Platform Beheer', items: [
                { label: 'Systeem Analytics', path: '/platform/dashboard', icon: LayoutDashboard },
                { label: 'Abonnement Overzicht', path: '/platform/subscriptions', icon: CreditCard },
                { label: 'Monitoring', path: '/platform/monitoring', icon: Activity },
                { label: 'Globale Instellingen', path: '/platform/settings', icon: Settings },
                { label: 'WK Poule Admin', path: '/platform/wkpoule', icon: Trophy },
            ]
        },
        {
            section: 'Klanten', items: [
                { label: 'Alle Klanten', path: '/platform/tenants', icon: Users },
                { label: 'Alle Toernooien', path: '/platform/tournaments', icon: Trophy },
            ]
        },
        {
            section: 'Informatie', items: [
                { label: 'Handleiding', path: '/platform/docs', icon: BookOpen },
            ]
        }
    ]

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">Checking Clearance...</div>

    return (
        <div className="flex h-screen bg-gray-100 overflow-hidden font-sans">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-20 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed md:relative z-30
                flex flex-col
                w-72 h-full
                bg-slate-900 text-slate-300
                transition-transform duration-300 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 md:w-20 lg:w-72'}
            `}>
                {/* Logo Area */}
                <div className="p-6 flex items-center gap-3 border-b border-slate-800">
                    <div className="bg-orange-500 p-2 rounded-lg">
                        <Shield className="text-white" size={24} />
                    </div>
                    <div className={`transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 md:hidden lg:opacity-100'}`}>
                        <h1 className="text-xl font-bold text-white tracking-tight">ProPoule</h1>
                        <span className="text-xs text-orange-400 uppercase font-bold tracking-wider">Super Admin</span>
                    </div>
                </div>

                {/* Back to Admin Button */}
                <div className="px-4 py-4 border-b border-slate-800">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-lg hover:shadow-blue-500/20"
                    >
                        <LayoutDashboard size={20} />
                        <span className={`font-semibold ${!sidebarOpen && 'md:hidden lg:block'}`}>
                            ← Terug naar Admin
                        </span>
                    </button>
                </div>

                {/* Menu Items */}
                <div className="flex-1 overflow-y-auto py-6 px-4 space-y-8">
                    {menuItems.map((section, idx) => (
                        <div key={idx}>
                            <h3 className={`text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-4 ${!sidebarOpen && 'md:hidden lg:block'}`}>
                                {section.section}
                            </h3>
                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const active = location.pathname === item.path
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`
                                                flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                                                ${active
                                                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                                                    : 'hover:bg-slate-800 hover:text-white'
                                                }
                                            `}
                                        >
                                            <item.icon size={20} className={active ? 'text-white' : 'text-slate-400'} />
                                            <span className={`${!sidebarOpen && 'md:hidden lg:block'}`}>
                                                {item.label}
                                            </span>
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* User Footer */}
                <div className="p-4 border-t border-slate-800">
                    <div className="bg-slate-800 rounded-xl p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-orange-400 to-red-500 flex items-center justify-center text-white font-bold">
                            P
                        </div>
                        <div className={`overflow-hidden ${!sidebarOpen && 'md:hidden lg:block'}`}>
                            <p className="text-sm font-bold text-white truncate max-w-[140px]">{user.email}</p>
                            <button onClick={handleLogout} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mt-1 transition-colors">
                                <LogOut size={12} /> Uitloggen
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Topbar (Mobile Toggle) */}
                <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6 md:hidden">
                    <button onClick={() => setSidebarOpen(true)} className="text-gray-600">
                        <Menu size={24} />
                    </button>
                </header>

                {/* Content */}
                <main className="flex-1 overflow-y-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    )
}
